// src/components/admin/performance/CompanyPerformanceAnalytics.js
import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Building, TrendingUp, BarChart3, Loader, AlertCircle, Filter } from 'lucide-react';
import performanceService from '../../../services/performance.service';

const CompanyPerformanceAnalytics = () => {
    const [allReviews, setAllReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cycleFilter, setCycleFilter] = useState('all');

    useEffect(() => {
        fetchAllReviews();
    }, []);

    const fetchAllReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await performanceService.getAllReviews();
            setAllReviews(data.reviews || []);
        } catch (err) {
            console.error('Error fetching reviews:', err);
            setError('Failed to load company performance data');
        } finally {
            setLoading(false);
        }
    };

    // Filter reviews
    const getFilteredReviews = () => {
        return allReviews.filter(review => {
            return (cycleFilter === 'all' || review.review_cycle === cycleFilter) && ['completed', 'confirmed'].includes(review.status);
        });
    };

    const filteredReviews = getFilteredReviews();

    // Prepare ratings distribution
    const prepareRatingsDistribution = () => {
        const distribution = {
            'Excellent (4.5-5.0)': 0,
            'Good (4.0-4.4)': 0,
            'Average (3.5-3.9)': 0,
            'Below Average (3.0-3.4)': 0,
            'Needs Improvement (<3.0)': 0
        };

        filteredReviews.forEach(review => {
            const rating = parseFloat(review.overall_rating) || 0;
            if (rating >= 4.5) distribution['Excellent (4.5-5.0)']++;
            else if (rating >= 4.0) distribution['Good (4.0-4.4)']++;
            else if (rating >= 3.5) distribution['Average (3.5-3.9)']++;
            else if (rating >= 3.0) distribution['Below Average (3.0-3.4)']++;
            else if (rating > 0) distribution['Needs Improvement (<3.0)']++;
        });

        return Object.entries(distribution).map(([name, value]) => ({ name, value }));
    };

    // Prepare trend data by month
    const prepareTrendData = () => {
        const monthlyData = {};

        filteredReviews.forEach(review => {
            const date = new Date(review.review_period_end);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                    totalReviews: 0,
                    totalRating: 0,
                    selfReviews: 0,
                    managerReviews: 0
                };
            }

            monthlyData[monthKey].totalReviews++;
            monthlyData[monthKey].totalRating += parseFloat(review.overall_rating) || 0;
            if (review.review_type === 'self_review') monthlyData[monthKey].selfReviews++;
            if (review.review_type === 'manager_review') monthlyData[monthKey].managerReviews++;
        });

        return Object.keys(monthlyData)
            .sort()
            .map(key => ({
                ...monthlyData[key],
                avgRating: (monthlyData[key].totalRating / monthlyData[key].totalReviews).toFixed(2)
            }));
    };

    // Prepare skills average data
    const prepareSkillsAverage = () => {
        const skills = ['technical_skills', 'communication_skills', 'teamwork', 'problem_solving', 'leadership'];
        const skillLabels = ['Technical', 'Communication', 'Teamwork', 'Problem Solving', 'Leadership'];

        return skillLabels.map((label, idx) => {
            const field = skills[idx];
            const values = filteredReviews
                .map(r => parseFloat(r[field]) || 0)
                .filter(v => v > 0);

            const avg = values.length > 0
                ? values.reduce((sum, v) => sum + v, 0) / values.length
                : 0;

            return { skill: label, average: parseFloat(avg.toFixed(2)) };
        });
    };

    // Prepare department comparison (mock data - in real scenario would come from API)
    const prepareDepartmentComparison = () => {
        const departments = {};

        filteredReviews.forEach(review => {
            const dept = review.department_name || 'Other';
            if (!departments[dept]) {
                departments[dept] = { totalRating: 0, count: 0 };
            }
            departments[dept].totalRating += parseFloat(review.overall_rating) || 0;
            departments[dept].count++;
        });

        return Object.keys(departments)
            .map(dept => ({
                department: dept,
                avgRating: (departments[dept].totalRating / departments[dept].count).toFixed(2),
                reviews: departments[dept].count
            }))
            .sort((a, b) => parseFloat(b.avgRating) - parseFloat(a.avgRating))
            .slice(0, 10);
    };

    // Calculate stats
    const calculateStats = () => {
        const totalReviews = filteredReviews.length;
        const selfReviews = filteredReviews.filter(r => r.review_type === 'self_review').length;
        const managerReviews = filteredReviews.filter(r => r.review_type === 'manager_review').length;

        const avgRating = totalReviews > 0
            ? filteredReviews.reduce((sum, r) => sum + (parseFloat(r.overall_rating) || 0), 0) / totalReviews
            : 0;

        const topPerformers = filteredReviews.filter(r => parseFloat(r.overall_rating) >= 4.5).length;

        return { totalReviews, selfReviews, managerReviews, avgRating, topPerformers };
    };

    const ratingsDistribution = prepareRatingsDistribution();
    const trendData = prepareTrendData();
    const skillsAverage = prepareSkillsAverage();
    const departmentComparison = prepareDepartmentComparison();
    const stats = calculateStats();

    const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
                    {label && <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{label}</p>}
                    {payload.map((entry, index) => (
                        <p key={index} className="text-xs text-gray-600 dark:text-gray-400">
                            <span style={{ color: entry.color }}>�</span> {entry.name}: {entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
        );
    }

    if (filteredReviews.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <Building className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Company Data</h3>
                <p className="text-gray-600 dark:text-gray-400">
                    No performance review data available for the selected filters.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <TrendingUp className="w-6 h-6" />
                        Company Performance Analytics
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Organization-wide performance insights and trends
                    </p>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-500" />
                        <select
                            value={cycleFilter}
                            onChange={(e) => setCycleFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Cycles</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="annual">Annual</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Reviews</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.totalReviews}</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Rating</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.avgRating.toFixed(1)}/5</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Self Reviews</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.selfReviews}</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Manager Reviews</p>
                    <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.managerReviews}</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Top Performers</p>
                    <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.topPerformers}</p>
                </div>
            </div>

            {/* Performance Trend Chart */}
            {trendData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Company Performance Trend
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={trendData}>
                            <defs>
                                <linearGradient id="colorRating" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis
                                dataKey="month"
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Area
                                type="monotone"
                                dataKey="avgRating"
                                stroke="#3B82F6"
                                fillOpacity={1}
                                fill="url(#colorRating)"
                                name="Average Rating"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ratings Distribution */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Ratings Distribution
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={ratingsDistribution}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name.split(' ')[0]}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {ratingsDistribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Average Skills Across Company */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Company-wide Skills Average
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={skillsAverage}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis
                                dataKey="skill"
                                stroke="#6B7280"
                                style={{ fontSize: '11px' }}
                                angle={-15}
                                textAnchor="end"
                                height={60}
                            />
                            <YAxis
                                domain={[0, 5]}
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="average" fill="#10B981" name="Average Rating" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Department Performance Comparison */}
            {departmentComparison.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Department Performance Comparison
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={departmentComparison}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis
                                dataKey="department"
                                stroke="#6B7280"
                                style={{ fontSize: '11px' }}
                                angle={-15}
                                textAnchor="end"
                                height={60}
                            />
                            <YAxis
                                domain={[0, 5]}
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="avgRating" fill="#3B82F6" name="Average Rating" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Review Volume Trend */}
            {trendData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Review Volume Trend
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis
                                dataKey="month"
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="selfReviews"
                                stroke="#3B82F6"
                                name="Self Reviews"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="managerReviews"
                                stroke="#10B981"
                                name="Manager Reviews"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default CompanyPerformanceAnalytics;
